import { Component, Injector, OnInit, ViewChild } from '@angular/core';
import { finalize, takeUntil } from 'rxjs/operators';
import { ApBaseModalComponent, ApNotificationService } from 'webcommon/core-ui';
import { MacroContextEnum, MacroDataSourceEnum, MacroDefinitionDto, MacroGroupDto } from 'webcommon/shared';

import { MacroRepository, MacroService } from '../ap-macro/index';

import { MacroInterfaces } from '../ap-macro/MacroInterfaces';
import { ApMacroEditorComponent } from './ap-macro-editor.component';

@Component({
  selector: 'ap-macro-editor-modal',
  templateUrl: './ap-macro-editor-modal.component.html',
})
export class ApMacroEditorModalComponent extends ApBaseModalComponent<boolean> implements OnInit {
  @ViewChild('macroEditor', { static: true }) macroEditor: ApMacroEditorComponent;

  action: string;
  macroService: MacroService;
  baseId: string;
  hasWarning = false;
  macroDefinitionDto: MacroDefinitionDto;
  macroInterfaces: MacroInterfaces;
  saving = false;
  title: string;
  defaultMacroGroup: MacroGroupDto | null = null;
  warningText: string;

  Id: string;

  origMacroName: string | null;

  constructor(
    elementInjector: Injector,
    private macroRepository: MacroRepository,
    private apNotificationService: ApNotificationService,
  ) {
    super(elementInjector);
  }

  override ngOnInit() {
    super.ngOnInit();
    this.setId();

    if (this.macroDefinitionDto) {
      this.origMacroName = this.macroDefinitionDto.Name;
    }
  }

  closeWarning() {
    this.hasWarning = false;
    this.warningText = '';
  }

  showWarning(): boolean {
    return this.hasWarning;
  }

  close() {
    if (this.saving) {
      return;
    }
    this.saving = true;
    if (!this.isMacroValidToSave()) {
      this.saving = false;
      return;
    }
    this.macroDefinitionDto.DataSource = MacroDataSourceEnum.Client;
    this.macroDefinitionDto.DataContext = MacroContextEnum.None;

    this.macroRepository.saveMacro(this.macroService.getPatientId(), this.macroDefinitionDto).pipe(
      finalize(() => {
        this.saving = false;
      }),
      takeUntil(this.onDestroy$),
    ).subscribe((macroDefinitionDto) => {
      this.saveComplete(macroDefinitionDto);
      super.closeModal(false);
    },
      (error) => {
        this.hasWarning = true;
        this.warningText = 'Failed to ' + this.action + ' macro ' + this.macroDefinitionDto.Name + ' !'
          + '<br />' + this.getErrorTextFromResponse(error);
      },
    );
  }

  private saveComplete(macroDefinitionDto: MacroDefinitionDto) {
    if (!macroDefinitionDto) { return; }
    if (!this.macroDefinitionDto.Id) {
      this.macroService.addMacroDefinitionDto(macroDefinitionDto);
    } else {
      this.macroService.onMacroListHasChanged();
    }
    this.showNotification(macroDefinitionDto.Name, this.origMacroName);
  }

  private showNotification(name: string | null, origMacroName: string | null) {
    if (this.action === 'create') {
      this.apNotificationService.success('Macro ' + name + ' created', '', 3000);
      return;
    }
    if (this.action === 'modify') {
      if (origMacroName !== name) {
        this.apNotificationService.success('Macro ' + origMacroName + ' renamed to ' + name, '', 3000);
        return;
      }
      this.apNotificationService.success('Macro ' + name + ' modified', '', 3000);
      return;
    }
    this.apNotificationService.success('Macro ' + name + ' cloned', '', 3000);
  }

  private isMacroValidToSave(): boolean {
    if (!this.macroEditor.isAllInputValid()) {
      return false;
    }
    if (!this.isNameValid()) {
      return false;
    }
    if (!this.isSaveAllowed()) {
      return false;
    }
    return true;
  }

  private isNameValid() {
    if (this.macroDefinitionDto.Name) {
      const match = this.macroDefinitionDto.Name.match('^[a-zA-Z][a-zA-Z0-9-_]+$');
      if (match && match.length > 0) {
        return true;
      } else {
        this.hasWarning = true;
        this.warningText =
          'Invalid Macro Name! <br /> The Name must start with an letter followed by alphanumeric characters, underscores or dashes.';
      }
    } else {
      this.hasWarning = true;
      this.warningText = 'Unable to ' + this.action + ' macro! <br /> A Name must be specified.';
    }
    return false;
  }
  private isSaveAllowed() {
    if (!this.macroService.isSaveAllowed(this.macroDefinitionDto)) {
      this.hasWarning = true;
      this.warningText =
        'Unable to ' + this.action + ' macro! <br /> A macro named ' + this.macroDefinitionDto.Name + ' is already defined';
      return false;
    }
    return true;
  }

  private getErrorTextFromResponse(error: any): string {
    const errMsg = error.error.ExceptionMessage ? error.error.ExceptionMessage
      : this.isResultError(error) ? this.getResultText(error)
        : error.status ? `${error.status} - ${error.statusText}` : 'Server error';
    return errMsg;
  }

  private isResultError(error: any): boolean {
    const x = error.error.ModelState.commandResult;
    return (x);
  }

  private getResultText(error: any): string {
    return error.error.ModelState.commandResult[0];

  }

  private setId() {
    this.Id = `${this.baseId}-macro-editor-modal`;
  }
}
